import { forwardRef, useMemo, type MouseEvent } from "react";

import { ChatBubbleOutline, Flag, Share } from "@mui/icons-material";
import { Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { colors, MediaCard, TruncatedText, type MediaCardItem } from "@rubi/ui";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { useCrimeLabel } from "@/api/loadAlerts";
import { formatDateShort, formatWeekdayShort } from "@/common/utils";
import { getBulletinPriorityColor } from "@/components/alerts/constants";
import { mediaEntityCaption } from "@/components/alerts/entities/mediaEntityCaption";
import { useServiceConfigurations } from "@/hooks/useCommunities";
import { BulletinResourceResponse } from "@/types";

type EvidenceFile = BulletinResourceResponse["evidence_files"][string];

type BulletinCardProps = {
  index: number;
  alertItem: BulletinResourceResponse;
  alertClickHandler: (
    alertItem: BulletinResourceResponse,
    index: number,
  ) => void;
  className?: string;
  selected?: boolean;
  pinned?: boolean;
  showFlag?: boolean;
};

const BulletinCard = forwardRef<HTMLDivElement, BulletinCardProps>(
  (
    {
      index,
      alertItem,
      alertClickHandler,
      className,
      selected,
      pinned,
      showFlag = true,
    },
    ref,
  ) => {
    const {
      evidence_files,
      associated_crime,
      issuing_police_service,
      occurrence_number,
      type,
      translated_type,
      priority,
      operational_area,
      issued_time,
      description,
      resolutions,
      flags,
      entities,
    } = alertItem;

    const navigate = useNavigate();
    const location = useLocation();
    const { t: entityLabel } = useTranslation("bulletin/entities");
    const crimeLabel = useCrimeLabel();
    const serviceConfigurations = useServiceConfigurations();

    const policeServiceName = issuing_police_service?.name;
    const getPoliceServiceName = useMemo(() => {
      const config = serviceConfigurations[policeServiceName];
      return config?.region || config?.shortName || policeServiceName;
    }, [policeServiceName, serviceConfigurations]);

    // Resolutions are surfaced to users as "comments" throughout the UI.
    const publishedResolutions = useMemo(() => {
      return resolutions
        .filter((resolution) => resolution.published)
        .sort(
          (a, b) =>
            new Date(b.time_updated).getTime() -
            new Date(a.time_updated).getTime(),
        );
    }, [resolutions]);
    const latestPublishedResolution = publishedResolutions[0];
    const commentCount = publishedResolutions.length;
    const latestComment = latestPublishedResolution?.details;

    const media = useMemo<MediaCardItem[]>(
      () =>
        Object.values(evidence_files ?? {})
          .sort((a, b) => (a.sort_ordinal ?? 0) - (b.sort_ordinal ?? 0))
          .filter(
            (file) =>
              file.content_type?.includes("image") ||
              file.content_type?.includes("video"),
          )
          .map((file: EvidenceFile) =>
            file.content_type?.includes("video")
              ? { type: "video", src: file.download_url }
              : {
                  type: "image",
                  src: file.thumbnail_url,
                  // Thumbnails are generated async; fall back to the original.
                  fallbackSrc: file.download_url,
                  caption: mediaEntityCaption(
                    file,
                    entities ?? [],
                    entityLabel,
                    {
                      dense: true,
                    },
                  ),
                },
          ),
      [evidence_files, entities, entityLabel],
    );

    const crimeTypes = (associated_crime ?? [])
      .map((crime) => crimeLabel(crime))
      .filter(Boolean);

    const isResolved = Boolean(latestPublishedResolution?.resolved);
    const titleColor = getBulletinPriorityColor(priority);
    const title = translated_type ?? type;

    const { statusText, statusTooltip } = useMemo(() => {
      if (!latestPublishedResolution?.reason) {
        return { statusText: "", statusTooltip: "" };
      }
      const reason = latestPublishedResolution.reason;
      const otherReason = latestPublishedResolution.other_reason;
      if (reason === "Other") {
        const fullText = otherReason || "Resolved";
        const badgeText =
          fullText.length > 15 ? `${fullText.slice(0, 15)}…` : fullText;
        return { statusText: badgeText, statusTooltip: fullText };
      }
      const shortReason = latestPublishedResolution.short_reason;
      const displayText = shortReason || reason;
      const badgeText =
        displayText.length > 15 ? `${displayText.slice(0, 15)}…` : displayText;
      return { statusText: badgeText, statusTooltip: reason };
    }, [latestPublishedResolution]);

    const incidentTitle =
      occurrence_number?.length > 0 ? occurrence_number.join(", ") : "";
    // Lead with the agency name, then the division/operational area.
    const divisionZone = [getPoliceServiceName, operational_area]
      .filter(Boolean)
      .join(" - ");

    const isFlagged = (flags?.length ?? 0) > 0;

    const handleOpen = () => {
      alertClickHandler(alertItem, index);
    };

    const handleShare = (event: MouseEvent) => {
      event.stopPropagation();
      const currentSearchParams = new URLSearchParams(location.search);
      currentSearchParams.set("bulletinId", alertItem.id.toString());
      currentSearchParams.set("access", "true");
      navigate(`${location.pathname}?${currentSearchParams.toString()}`);
    };

    return (
      <MediaCard
        ref={ref}
        className={`alert-item ${className ?? ""}`}
        onClick={handleOpen}
        title={title}
        accentColor={titleColor}
        selected={selected}
        pinned={pinned}
        titleStrikethrough={isResolved}
        media={media}
        subheader={
          crimeTypes.length > 0 && (
            <Stack direction="row" spacing={0.5} sx={{ overflow: "hidden" }}>
              {crimeTypes.slice(0, 2).map((crime) =>
                crimeTypes.length > 2 ? (
                  <Tooltip key={crime} title={crime}>
                    <Chip
                      label={crime}
                      size="small"
                      sx={{
                        minWidth: 0,
                        bgcolor: alpha(colors.common.white, 0.6),
                        "& .MuiChip-label": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      }}
                    />
                  </Tooltip>
                ) : (
                  <Chip
                    key={crime}
                    label={crime}
                    size="small"
                    sx={{ bgcolor: alpha(colors.common.white, 0.6) }}
                  />
                ),
              )}
              {crimeTypes.length > 2 && (
                <Tooltip title={crimeTypes.slice(2).join(", ")}>
                  <Chip
                    label={`+${crimeTypes.length - 2}`}
                    size="small"
                    sx={{
                      flexShrink: 0,
                      bgcolor: alpha(colors.common.white, 0.6),
                    }}
                  />
                </Tooltip>
              )}
            </Stack>
          )
        }
        headerAction={
          statusText && (
            <Tooltip title={statusTooltip} placement="top" arrow>
              <Typography
                variant="subtitle2"
                sx={{
                  ml: 1,
                  my: -0.5,
                  flexShrink: 0,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  color: "error.main",
                  border: 2,
                  borderColor: "error.main",
                  borderRadius: 1,
                  px: 0.75,
                  py: 0.25,
                }}
              >
                {statusText}
              </Typography>
            </Tooltip>
          )
        }
        emptyContent={
          <Typography
            variant="body2"
            sx={{ position: "absolute", inset: 0, p: 1.5, overflow: "auto" }}
          >
            {description}
          </Typography>
        }
        mediaOverlay={
          <>
            {showFlag && isFlagged && (
              <Flag
                sx={{
                  position: "absolute",
                  top: 8,
                  left: 8,
                  color: "error.main",
                }}
              />
            )}
            <IconButton
              size="small"
              onClick={handleShare}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.85),
                opacity: 0,
                transition: "opacity .2s",
                ".alert-item:hover &": { opacity: 1 },
                "&:hover": {
                  bgcolor: (theme) =>
                    alpha(theme.palette.background.paper, 0.95),
                },
              }}
            >
              <Share fontSize="small" />
            </IconButton>
          </>
        }
        footer={
          <>
            {incidentTitle && (
              <TruncatedText variant="subtitle1" sx={{ fontWeight: 700 }}>
                {incidentTitle}
              </TruncatedText>
            )}
            {issued_time && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {formatDateShort(issued_time)}{" "}
                <i>({formatWeekdayShort(issued_time)})</i>
              </Typography>
            )}
            {divisionZone && (
              <TruncatedText variant="body2" sx={{ color: "text.secondary" }}>
                {divisionZone}
              </TruncatedText>
            )}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                mt: 1,
                px: 1.5,
                py: 0.75,
                borderRadius: 2,
                bgcolor: "action.hover",
                color: "text.secondary",
              }}
            >
              <ChatBubbleOutline fontSize="small" />
              <Typography variant="body2" sx={{ color: "text.primary" }}>
                {commentCount}
              </Typography>
              {latestComment ? (
                <TruncatedText variant="body2" sx={{ minWidth: 0 }}>
                  {latestComment}
                </TruncatedText>
              ) : (
                <Typography
                  variant="body2"
                  sx={{ fontStyle: "italic", color: "text.disabled" }}
                >
                  No comments yet.
                </Typography>
              )}
            </Stack>
          </>
        }
      />
    );
  },
);

BulletinCard.displayName = "BulletinCard";

export default BulletinCard;
